import { formatFieldDisplayValue } from './productChatConfig';
import { mapExtractedQuote } from './mapExtractedQuote';
import { normalizeQuoteProducts } from './normalizeQuoteProduct';

function defaultProduct() {
  return {
    productType: '',
    SKU: 1,
    quantitiesCount: 1,
    skuDetails: [{
      sku: 1,
      quantities: [{ quantity: 1, value: '' }],
    }],
    fields: {},
    address: '',
    packagingInstructions: '',
    shippingInstructions: '',
    specialNotesToProduction: '',
    imageUrls: [],
    printingStyle: '',
    productionType: '',
    colorMatch: 'No',
    isReprint: 'No',
  };
}

function buildSkuDetails(product) {
  const SKU = Math.max(1, parseInt(product.SKU, 10) || 1);
  const quantitiesCount = Math.max(1, parseInt(product.quantitiesCount, 10) || 1);

  if (Array.isArray(product.skuDetails) && product.skuDetails.length) {
    return product.skuDetails.map((sku, skuIndex) => ({
      sku: skuIndex + 1,
      quantities: (sku.quantities || []).map((qty, qtyIndex) => ({
        quantity: qtyIndex + 1,
        value: qty.value ?? qty ?? '',
      })),
    }));
  }

  if (Array.isArray(product.skuQuantities) && product.skuQuantities.length) {
    const skuCount = Math.max(1, parseInt(product.SKU, 10) || product.skuQuantities.length);
    const rows = product.skuQuantities.map((row) => (Array.isArray(row) ? row : [row]));
    const quantityTiers = rows[0] || [];

    if (rows.length === 1 && skuCount > 1) {
      const tiers = quantityTiers.map((val) => val ?? '');
      return Array.from({ length: skuCount }, (_, skuIndex) => ({
        sku: skuIndex + 1,
        quantities: tiers.map((val, qtyIndex) => ({
          quantity: qtyIndex + 1,
          value: val,
        })),
      }));
    }

    return rows.map((row, skuIndex) => ({
      sku: skuIndex + 1,
      quantities: row.map((val, qtyIndex) => ({
        quantity: qtyIndex + 1,
        value: val ?? '',
      })),
    }));
  }

  return Array.from({ length: SKU }, (_, skuIndex) => ({
    sku: skuIndex + 1,
    quantities: Array.from({ length: quantitiesCount }, (_, qtyIndex) => ({
      quantity: qtyIndex + 1,
      value: '',
    })),
  }));
}

function normalizeProductSkeleton(product) {
  const skuDetails = buildSkuDetails(product);
  return {
    productType: product.productType || '',
    SKU: skuDetails.length || 1,
    quantitiesCount: skuDetails[0]?.quantities?.length || 1,
    skuDetails,
    fields: product.fields && typeof product.fields === 'object' ? product.fields : {},
    address: product.address || '',
    packagingInstructions: product.packagingInstructions || '',
    shippingInstructions: product.shippingInstructions || '',
    specialNotesToProduction: product.specialNotesToProduction || '',
    imageUrls: [],
    printingStyle: product.printingStyle || '',
    productionType: product.productionType || '',
    colorMatch: product.colorMatch === 'Yes' ? 'Yes' : 'No',
    isReprint: product.isReprint === 'Yes' ? 'Yes' : 'No',
    colorMatchOrderNumber: product.colorMatchOrderNumber || '',
    previousInvoiceNumber: product.previousInvoiceNumber || '',
  };
}

export function normalizeExtractedQuote(extracted, options = {}) {
  const mapped = mapExtractedQuote(extracted, options);
  const skeletons = (mapped.products || []).map(normalizeProductSkeleton);
  const products = normalizeQuoteProducts(skeletons);

  const missingFields = Array.isArray(mapped.missingFields) ? [...mapped.missingFields] : [];
  if (!mapped.companyName?.trim() && !missingFields.includes('Company name')) {
    missingFields.push('Company name');
  }

  return {
    companyName: mapped.companyName || '',
    projectName: mapped.projectName || '',
    projectDescription: mapped.projectDescription || '',
    accountStatus: mapped.accountStatus || '',
    isVIPClient: !!mapped.isVIPClient,
    products: products.length ? products : [defaultProduct()],
    summary: mapped.summary || '',
    missingFields,
    notes: mapped.notes || '',
  };
}

export function buildPrefilledQuoteForStepOne(draft) {
  return {
    companyName: draft.companyName,
    projectName: draft.projectName,
    projectDescription: draft.projectDescription,
    accountStatus: draft.accountStatus,
    isVIPClient: draft.isVIPClient,
    products: draft.products,
  };
}

function formatFieldValue(key, value) {
  if (value == null || value === '') return null;
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    if (value.widthMM && value.heightMM) {
      const g = value.gussetMM || value.depthMM || value.depth;
      return g
        ? `${value.widthMM} x ${value.heightMM} x ${g} mm`
        : `${value.widthMM} x ${value.heightMM} mm`;
    }
    return null;
  }
  return formatFieldDisplayValue(key, value) || String(value);
}

const FIELD_LABELS = {
  bagType: 'Bag type',
  boxType: 'Box type',
  blisterType: 'Blister type',
  capType: 'Cap type',
  material: 'Material',
  printingFinishes: 'Printing finish',
  printingFeatures: 'Printing features',
  bagSpecifications: 'Bag features',
  closureType: 'Closure',
  zipperType: 'Zipper type',
  width: 'Width (mm)',
  height: 'Height (mm)',
  gusset: 'Gusset (mm)',
  depth: 'Depth (mm)',
  shape: 'Shape',
  finish: 'Finish',
  applyMethod: 'Apply method',
  rollingDirection: 'Rolling direction',
};

export function formatDraftForReview(draft) {
  const lines = [
    `Company: ${draft.companyName || '—'}`,
    `Project: ${draft.projectName || '—'}`,
    `Description: ${draft.projectDescription || '—'}`,
  ];
  if (draft.accountStatus) lines.push(`Account status: ${draft.accountStatus}`);
  if (draft.isVIPClient) lines.push('VIP client: Yes');

  draft.products.forEach((product, index) => {
    lines.push('');
    lines.push(`Product ${index + 1}: ${product.productType || '—'}`);
    if (product.printingStyle) lines.push(`  Printing style: ${product.printingStyle}`);
    if (product.productionType) lines.push(`  Production: ${product.productionType}`);
    if (product.colorMatch === 'Yes') lines.push('  Color match: Yes');
    if (product.isReprint === 'Yes') lines.push('  Reprint: Yes');
    if (product.sizeNote) lines.push(`  Size: ${product.sizeNote}`);
    if (product.specialNotesToProduction) {
      lines.push(`  Notes: ${product.specialNotesToProduction}`);
    }

    Object.entries(product.fields || {}).forEach(([key, value]) => {
      const formatted = formatFieldValue(key, value);
      if (!formatted) return;
      const label = FIELD_LABELS[key] || key;
      lines.push(`  ${label}: ${formatted}`);
    });

    if (product.skuDetails?.length) {
      const qtyLine = product.skuDetails.map((sku) => {
        const vals = sku.quantities?.map((q) => q.value).filter(Boolean);
        return vals?.length ? `SKU ${sku.sku}: ${vals.join(', ')}` : null;
      }).filter(Boolean);
      if (qtyLine.length) lines.push(`  Quantities: ${qtyLine.join(' | ')}`);
    }
  });

  return lines.join('\n');
}
